import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { GET } from "../../services/axios.service";
import { RootState } from "../../app/store";
import { partyText } from "../../static/materialLine";
import { PartyOptionProps } from "../../types/materialLine";
import {
  VmcOrderDateBy,
  VmcOrderListProps,
  VmcOrderProps,
  VmcOrderSortBy,
  VmcOrderSortOrder,
  VmcOrderState,
  VmcPartyInfoProps,
} from "../../types/vmcOrder";

// VMC orders, like the turning orders slice: no material list, and the party info carries the pieces ready of both
// kinds (cut and turned) instead of the party's material.
export interface VmcOrdersState {
  orders: VmcOrderListProps[];
  totalOrders: number;
  currentOrder: VmcOrderProps | null;
  currentOrderLoading: boolean;
  partyOptions: PartyOptionProps[];
  filterPartyOptions: PartyOptionProps[];
  partyInfo: VmcPartyInfoProps;
  partyInfoLoading: boolean;
  partyInfoRequestId: string | null;
  loading: boolean;
  error: string | null;
}

const emptyPartyInfo: VmcPartyInfoProps = {
  inhouse: false,
  options: [],
  ready: {},
};

const initialState: VmcOrdersState = {
  orders: [],
  totalOrders: 0,
  currentOrder: null,
  currentOrderLoading: false,
  partyOptions: [],
  filterPartyOptions: [],
  partyInfo: emptyPartyInfo,
  partyInfoLoading: false,
  partyInfoRequestId: null,
  loading: false,
  error: null,
};

const defaultError = "કંઈક ખોટું ગયું, કૃપા કરીને ફરીથી પ્રયત્ન કરો.";

type FetchVmcOrderListDataProps = {
  page: number;
  size?: number;
  search?: string | null;
  fromDate?: string | null;
  toDate?: string | null;
  party_id?: number | null;
  status?: VmcOrderState | null;
  date_by?: VmcOrderDateBy;
  sort_by?: VmcOrderSortBy;
  sort_order?: VmcOrderSortOrder;
};

export const fetchVmcOrderListData = createAsyncThunk(
  "fetchVmcOrderListData",
  async (req: FetchVmcOrderListDataProps) => {
    let response: any;
    await GET(`/v1/admin/vmc-orders`, { ...req })
      .then((res: any) => {
        response = res;
      })
      .catch((err) => {
        response = err;
      });
    return response;
  }
);

export const fetchVmcOrderDetail = createAsyncThunk(
  "fetchVmcOrderDetail",
  async (id: number) => {
    let response: any;
    await GET(`/v1/admin/vmc-orders/${id}`, {})
      .then((res: any) => {
        response = res;
      })
      .catch((err) => {
        response = err;
      });
    return response;
  }
);

// Loads the open order again without clearing it first, so the page keeps showing it (no loader), e.g. after one of its
// receives was deleted.
export const refreshVmcOrderDetail = createAsyncThunk(
  "refreshVmcOrderDetail",
  async (id: number) => {
    let response: any;
    await GET(`/v1/admin/vmc-orders/${id}`, {})
      .then((res: any) => {
        response = res;
      })
      .catch((err) => {
        response = err;
      });
    return response;
  }
);

// partyId keeps an edited order's own party in the list.
export const fetchVmcOrderParties = createAsyncThunk(
  "fetchVmcOrderParties",
  async (partyId?: number | null) => {
    let response: any;
    await GET(`/v1/admin/vmc-orders/parties`, {
      party_id: partyId || null,
    })
      .then((res: any) => {
        response = res;
      })
      .catch((err) => {
        response = err;
      });
    return response;
  }
);

// The list filter's parties: every VMC party, active or not, and every party that has orders.
export const fetchVmcOrderFilterParties = createAsyncThunk(
  "fetchVmcOrderFilterParties",
  async () => {
    let response: any;
    await GET(`/v1/admin/vmc-orders/parties`, { all: 1 })
      .then((res: any) => {
        response = res;
      })
      .catch((err) => {
        response = err;
      });
    return response;
  }
);

// The items the party can be asked to machine, once per kind, and the pieces ready; order_id counts the edited order's
// own lines as ready for it.
export const fetchVmcOrderPartyInfo = createAsyncThunk(
  "fetchVmcOrderPartyInfo",
  async (req: { party_id: number; order_id?: number | null }) => {
    let response: any;
    await GET(`/v1/admin/vmc-orders/party-info`, { ...req })
      .then((res: any) => {
        response = res;
      })
      .catch((err) => {
        response = err;
      });
    return response;
  }
);

export const vmcOrdersSlice = createSlice({
  name: "vmcOrders",
  initialState,
  reducers: {
    clearCurrentVmcOrder: (state) => {
      state.currentOrder = null;
      state.currentOrderLoading = false;
    },
    clearVmcOrderPartyInfo: (state) => {
      state.partyInfo = emptyPartyInfo;
      state.partyInfoLoading = false;
      state.partyInfoRequestId = null;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(fetchVmcOrderListData.pending, (state) => {
      state.orders = [];
      state.loading = true;
      state.error = null;
    });
    builder.addCase(
      fetchVmcOrderListData.fulfilled,
      (state, action: any) => {
        state.loading = false;
        if (action.payload.data.status === "success") {
          state.orders = action.payload.data.data.records;
          state.totalOrders = action.payload.data.data.total_records;
          state.error = null;
        } else {
          state.orders = [];
          state.error = action?.payload?.data?.message || defaultError;
        }
      }
    );
    builder.addCase(
      fetchVmcOrderListData.rejected,
      (state, action: any) => {
        state.orders = [];
        state.loading = false;
        state.error = action?.payload?.data?.message || defaultError;
      }
    );

    builder.addCase(fetchVmcOrderDetail.pending, (state) => {
      state.currentOrderLoading = true;
      state.currentOrder = null;
    });
    builder.addCase(fetchVmcOrderDetail.fulfilled, (state, action: any) => {
      state.currentOrderLoading = false;
      if (action.payload.data.status === "success") {
        state.currentOrder = action.payload.data.data;
      }
    });
    builder.addCase(fetchVmcOrderDetail.rejected, (state) => {
      state.currentOrderLoading = false;
      state.currentOrder = null;
    });

    builder.addCase(refreshVmcOrderDetail.fulfilled, (state, action: any) => {
      if (action.payload?.data?.status === "success") {
        state.currentOrder = action.payload.data.data;
      }
    });

    builder.addCase(fetchVmcOrderParties.fulfilled, (state, action: any) => {
      if (action.payload.data.status === "success") {
        state.partyOptions = action.payload.data.data.records.map(
          (party: any) => ({ value: party.id, label: partyText(party) })
        );
      }
    });

    builder.addCase(
      fetchVmcOrderFilterParties.fulfilled,
      (state, action: any) => {
        if (action.payload.data.status === "success") {
          state.filterPartyOptions = action.payload.data.data.records.map(
            (party: any) => ({ value: party.id, label: partyText(party) })
          );
        }
      }
    );

    // Only the latest request counts, so a slow answer for a party picked earlier can't overwrite it.
    builder.addCase(fetchVmcOrderPartyInfo.pending, (state, action) => {
      state.partyInfo = emptyPartyInfo;
      state.partyInfoLoading = true;
      state.partyInfoRequestId = action.meta.requestId;
    });
    builder.addCase(
      fetchVmcOrderPartyInfo.fulfilled,
      (state, action: any) => {
        if (action.meta.requestId !== state.partyInfoRequestId) return;
        state.partyInfoLoading = false;
        state.partyInfo =
          action.payload.data.status === "success"
            ? { ...emptyPartyInfo, ...action.payload.data.data }
            : emptyPartyInfo;
      }
    );
    builder.addCase(fetchVmcOrderPartyInfo.rejected, (state, action) => {
      if (action.meta.requestId !== state.partyInfoRequestId) return;
      state.partyInfoLoading = false;
    });
  },
});

export const { clearCurrentVmcOrder, clearVmcOrderPartyInfo } =
  vmcOrdersSlice.actions;

export const getVmcOrders = (state: RootState) =>
  state.vmcOrders.orders;
export const getTotalVmcOrders = (state: RootState) =>
  state.vmcOrders.totalOrders;
export const getCurrentVmcOrder = (state: RootState) =>
  state.vmcOrders.currentOrder;
export const getCurrentVmcOrderLoading = (state: RootState) =>
  state.vmcOrders.currentOrderLoading;
export const getVmcOrderPartyOptions = (state: RootState) =>
  state.vmcOrders.partyOptions;
export const getVmcOrderFilterPartyOptions = (state: RootState) =>
  state.vmcOrders.filterPartyOptions;
export const getVmcOrderPartyInfo = (state: RootState) =>
  state.vmcOrders.partyInfo;
export const getVmcOrderPartyInfoLoading = (state: RootState) =>
  state.vmcOrders.partyInfoLoading;

export default vmcOrdersSlice.reducer;
